import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Typography,
} from '@mui/material';
import { Mic as RecordIcon, EditNote as WriteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Every "Start Session" goes through here: ask how many AI sessions are left BEFORE
 * opening the recorder, then how the visit is to be documented — recorded, or written by
 * hand with no recording. None left → say so and offer only the hand-written note, which
 * uses no session; a clinic out of sessions can still document its visits. A few left →
 * say how many and let them carry on.
 *
 *   const { startSession, gateDialog } = useStartSession();
 *   <Button onClick={() => startSession(patientId)}>Start Session</Button>
 *   {gateDialog}
 */
export function useStartSession() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);     // { kind: 'choose', patientId, blocked, low, remaining }
  const [checking, setChecking] = useState(false);

  const [creating, setCreating] = useState(false);
  const go = (patientId) => navigate(`/scribe/${patientId}`);

  // No recording: the visit is created with an empty note and opened straight into the
  // editor, where each section is typed or dictated.
  const writeManually = async (patientId) => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/clinical/session/start`,
        { patient_id: patientId, manual: true });
      setState(null);
      navigate(`/review/${data.session_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the visit');
    } finally {
      setCreating(false);
    }
  };

  const startSession = async (patientId) => {
    if (checking) return;
    setChecking(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/clinical/ai-budget`);
      setState({ kind: 'choose', patientId, blocked: !!data.blocked,
                 low: !!data.low, remaining: data.remaining });
    } catch {
      // The check is advisory; the server refuses at session start regardless.
      setState({ kind: 'choose', patientId });
    } finally {
      setChecking(false);
    }
  };

  const isAdmin = user?.role === 'clinic_admin';
  const gateDialog = (
    <Dialog open={Boolean(state)} onClose={() => setState(null)} maxWidth="xs" fullWidth>
      {state?.kind === 'choose' ? (
        <>
          <DialogTitle>How do you want to document this visit?</DialogTitle>
          <DialogContent>
            {state.blocked ? (
              <Alert severity="error" sx={{ mb: 2 }}
                action={isAdmin && (
                  <Button color="inherit" size="small"
                    onClick={() => { setState(null); navigate('/credits'); }}>
                    AI Sessions
                  </Button>
                )}>
                No AI sessions left, so the visit can’t be recorded.{' '}
                {isAdmin ? 'Request more from AI Sessions.' : 'Ask your clinic administrator to request more.'}
                {' '}You can still write the note yourself.
              </Alert>
            ) : state.low && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Only {state.remaining} AI session{state.remaining === 1 ? '' : 's'} left — recording
                this visit will use one of them.
              </Alert>
            )}
            <Stack spacing={1.5}>
              {[
                { key: 'record', icon: <RecordIcon />, title: 'Record the visit',
                  text: 'Record the consultation and the AI writes the note for you to review. Uses one AI session.',
                  disabled: state.blocked,
                  onClick: () => { const p = state.patientId; setState(null); go(p); } },
                { key: 'write', icon: <WriteIcon />, title: 'Write the note myself',
                  text: state.blocked
                    ? 'No recording, no AI session. Opens an empty note to type each section yourself.'
                    : 'No recording. Opens an empty note — type each section, or dictate into one (dictating uses an AI session).',
                  onClick: () => writeManually(state.patientId) },
              ].map((o) => (
                <ButtonBase key={o.key} onClick={o.onClick} disabled={creating || o.disabled}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.75, textAlign: 'left',
                        border: '1px solid', borderColor: 'divider', borderRadius: 2, width: '100%',
                        opacity: o.disabled ? 0.45 : 1,
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}>
                  <Box sx={{ color: 'primary.main', mt: 0.25 }}>
                    {creating && o.key === 'write' ? <CircularProgress size={20} /> : o.icon}
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>{o.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{o.text}</Typography>
                  </Box>
                </ButtonBase>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setState(null)}>Cancel</Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );

  return { startSession, gateDialog, checking };
}

export default useStartSession;
